import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { useSiteContent } from "../../context/SiteContent";

// Splits "50+ MWp" -> { num:50, suffix:"+ MWp" }, "12M+ Liters" -> {num:12, suffix:"M+ Liters"}
function parseMetric(str) {
  const s = String(str || "").trim();
  const m = s.match(/^([\d,]+(?:\.\d+)?)(.*)$/);
  if (!m) return { num: 0, suffix: s, decimals: 0 };
  const numStr = m[1].replace(/,/g, "");
  const decimals = numStr.includes(".") ? (numStr.split(".")[1].length) : 0;
  return { num: parseFloat(numStr), suffix: m[2], decimals };
}

function Counter({ raw }) {
  const { num, suffix, decimals } = parseMetric(raw);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, num, { duration: 1.8, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => setDisplay(v) });
    return () => controls.stop();
  }, [inView, num]);

  const shown = decimals ? display.toFixed(decimals) : Math.round(display).toLocaleString("en-IN");
  return (
    <span ref={ref} className="font-mono font-bold tracking-tight text-4xl md:text-5xl text-white">
      {shown}<span className="text-helios-amber">{suffix}</span>
    </span>
  );
}

export const ImpactCounter = () => {
  const { content } = useSiteContent();
  const ic = content?.impactCounters || {};
  const metrics = [
    { v: ic.mwCleaned, label: "Cumulative Solar Capacity Automated & Maintained" },
    { v: ic.cyclesExecuted, label: "Autonomous Cleaning Cycles Completed" },
    { v: ic.waterSaved, label: "Industrial Water Saved to Date" },
    { v: ic.dustRemovalPct, label: "Average Dust & Soiling Elimination Efficiency" },
  ];

  return (
    <section className="relative py-20 md:py-24 bg-helios-slate overflow-hidden" data-testid="impact-counter">
      <div className="noise-overlay" />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
          {metrics.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}
              className={`${i !== 0 ? "lg:border-l lg:border-white/10 lg:pl-8" : ""}`}
              data-testid={`impact-metric-${i}`}>
              <Counter raw={m.v} />
              <p className="mt-3 text-sm text-slate-400 leading-snug max-w-[220px]">{m.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
